import Decimal from "decimal.js-light";
import { plotData as plotDataFliteboardSI } from "../graph-data/SI/data-fliteboard";
import { plotData as plotDataFlitescooterSI } from "../graph-data/SI/data-flitescooter";
import { plotData as plotDataFliteboardUS } from "../graph-data/US/data-fliteboard";
import { plotData as plotDataFlitescooterUS } from "../graph-data/US/data-flitescooter";
import { shopifyConfig } from "./shopifyConfig";

type PlotRow = (typeof plotDataFliteboardSI)[number];

let plotDataFliteboard: PlotRow[];
let plotDataFlitescooter: PlotRow[];

switch (shopifyConfig.units) {
  case "SI":
    plotDataFliteboard = plotDataFliteboardSI;
    plotDataFlitescooter = plotDataFlitescooterSI;
    break;
  case "US":
    plotDataFliteboard = plotDataFliteboardUS;
    plotDataFlitescooter = plotDataFlitescooterUS;
    break;
  default:
    plotDataFliteboard = plotDataFliteboardSI;
    plotDataFlitescooter = plotDataFlitescooterSI;
    break;
}

export enum BoardType {
  Fliteboard = "Fliteboard",
  Flitescooter = "Flitescooter",
}

export enum WingType {
  // Cruiser1100 = "Cruiser1100",
  Cruiser1800 = "Cruiser1800",
  // CruiserJet1500 = "CruiserJet1500",
  CruiserJet1500C = "CruiserJet1500C",
  // CruiserJet1800 = "CruiserJet1800",
  // Flow1100 = "Flow1100",
  // Flow1300 = "Flow1300",
  // FlowS1100 = "FlowS1100",
  // FlowS1300 = "FlowS1300",
  Flux707C = "Flux707C",
  // Flux808C = "Flux808C",
  // Flux909BC = "Flux909BC",
  Flux1010C = "Flux1010C",
  // Flyer800 = "Flyer800",
  MN1300C = "MN1300C",
  Wave850C = "Wave850C",
  // Race700 = "Race700",
}

export enum FliteCell {
  Explore = "Explore",
  Sport = "Sport",
  Nano = "Nano",
}

export enum Propeller {
  TrueGlideProp = "TrueGlideProp",
  PropAndGuard = "PropAndGuard",
  Jet1 = "Jet1",
  Jet2 = "Jet2",
}

export type GraphDatum = {
  propeller: Propeller;
  averageSpeed: Speed;
  foilingTime: number;
};

type WeightSI = 50 | 55 | 60 | 65 | 70 | 75 | 80 | 85 | 90 | 95 | 100;
type WeightUS = 120 | 140 | 160 | 180 | 200 | 220;

export type Weight = WeightSI | WeightUS;
type Speed = number;

type UserConfiguration = {
  boardType: BoardType;
  weight: Weight;
  wingType: WingType;
  fliteCell: FliteCell;
  propeller: Propeller;
};

type FoilingTimeInput = UserConfiguration & {
  averageSpeed: Speed;
};

let config = {
  weight: 0,
  boardType: BoardType.Fliteboard,
  wingType: WingType.CruiserJet1500C,
  fliteCell: FliteCell.Explore,
};

let data = {} as { [key in Propeller]: PlotRow | [] };

function getPlotData(boardType: BoardType) {
  switch (boardType) {
    case BoardType.Fliteboard:
      return plotDataFliteboard;
    case BoardType.Flitescooter:
      return plotDataFlitescooter;
    default:
      return plotDataFliteboard;
  }
}

export function getFoilingTime(input: FoilingTimeInput): Speed;

export function getFoilingTime(input: FoilingTimeInput): Speed {
  const { weight, wingType, fliteCell, propeller, averageSpeed, boardType } =
    input;

  if (
    !(
      config.boardType === boardType &&
      config.fliteCell === fliteCell &&
      config.wingType === wingType &&
      config.weight === weight
    )
  ) {
    const plotData = getPlotData(boardType);
    config = { weight, wingType, fliteCell, boardType };

    let propellers = [] as Propeller[];
    if (boardType === BoardType.Fliteboard) {
      propellers = [
        Propeller.Jet2,
        Propeller.Jet1,
        Propeller.PropAndGuard,
        Propeller.TrueGlideProp,
      ];
    } else if (boardType === BoardType.Flitescooter) {
      propellers = [Propeller.Jet2, Propeller.Jet1];
    }

    data = {} as { [key in Propeller]: PlotRow | [] };

    for (const propeller of propellers) {
      data[propeller] =
        plotData.find((d) => d[0] === `${weight}${wingType}${propeller}`) ?? [];
    }
  }

  let kph = averageSpeed / (shopifyConfig.units === "SI" ? 1 : 0.6213711922);

  const minSpeed = Number(data[propeller][1]);
  const maxSpeed = Number(data[propeller][6]);
  const p0 = new Decimal(data[propeller][2]);
  const p1 = new Decimal(data[propeller][3]);
  const p2 = new Decimal(data[propeller][4]);
  const p3 = new Decimal(data[propeller][5]);

  const kph1 = kph;
  const kph2 = kph1 * kph;
  const kph3 = kph2 * kph;

  if (kph > maxSpeed || kph < minSpeed) return 0;

  const power = p3.add(p2.mul(kph1)).add(p1.mul(kph2)).add(p0.mul(kph3));

  if (power.toNumber() === 0) return 0;
  const foilingTime = new Decimal(fliteCellDuration(fliteCell))
    .dividedBy(power)
    .toNumber();

  return foilingTime;
}
function fliteCellDuration(fliteCell: FliteCell) {
  switch (fliteCell) {
    case FliteCell.Explore:
      return 1800;
    case FliteCell.Sport:
      return 1250;
    case FliteCell.Nano:
      return 700;
  }
}
